import { DatadogProvider } from '@datadog/openfeature-browser';
import { OpenFeature, ProviderEvents } from '@openfeature/web-sdk';
import type { EvaluationContext } from '@openfeature/web-sdk';

import { ANONYMOUS_FEATURE_FLAG_CONTEXT } from './types';

const FEATURE_FLAG_LOG_PREFIX = '[feature-flags]';
let handlersRegistered = false;
let fetchLoggingRegistered = false;

function getFeatureFlagEnvSummary() {
  return {
    env: process.env.REACT_APP_DD_ENV || 'unset',
    hasApplicationId: Boolean(process.env.REACT_APP_DD_APPLICATION_ID),
    hasClientToken: Boolean(process.env.REACT_APP_DD_CLIENT_TOKEN),
    site: process.env.REACT_APP_DD_SITE || 'unset',
  };
}

function registerFeatureFlagEventHandlers() {
  if (handlersRegistered) {
    return;
  }

  handlersRegistered = true;

  OpenFeature.addHandler(ProviderEvents.Ready, () => {
    console.info(`${FEATURE_FLAG_LOG_PREFIX} provider ready`);
  });

  OpenFeature.addHandler(ProviderEvents.Error, (details) => {
    console.error(`${FEATURE_FLAG_LOG_PREFIX} provider error`, details);
  });

  OpenFeature.addHandler(ProviderEvents.Reconciling, (details) => {
    console.info(`${FEATURE_FLAG_LOG_PREFIX} provider reconciling`, details);
  });

  OpenFeature.addHandler(ProviderEvents.ContextChanged, (details) => {
    console.info(
      `${FEATURE_FLAG_LOG_PREFIX} provider context changed`,
      details
    );
  });

  OpenFeature.addHandler(ProviderEvents.Stale, (details) => {
    console.warn(`${FEATURE_FLAG_LOG_PREFIX} provider stale`, details);
  });
}

function isFeatureFlagRequest(input: RequestInfo | URL) {
  const requestUrl =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input.url;

  return requestUrl.includes('precompute-assignments');
}

function registerFeatureFlagFetchLogging() {
  if (
    fetchLoggingRegistered ||
    typeof window === 'undefined' ||
    !window.fetch
  ) {
    return;
  }

  fetchLoggingRegistered = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function featureFlagLoggingFetch(input, init) {
    if (!isFeatureFlagRequest(input)) {
      return originalFetch(input, init);
    }

    const startedAt = Date.now();
    console.info(`${FEATURE_FLAG_LOG_PREFIX} request started`, {
      method: (init && init.method) || 'GET',
      url:
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url,
    });

    try {
      const response = await originalFetch(input, init);

      console.info(`${FEATURE_FLAG_LOG_PREFIX} request completed`, {
        durationMs: Date.now() - startedAt,
        ok: response.ok,
        status: response.status,
        url:
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.toString()
              : input.url,
      });

      return response;
    } catch (error) {
      console.error(`${FEATURE_FLAG_LOG_PREFIX} request failed`, {
        durationMs: Date.now() - startedAt,
        error,
        url:
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.toString()
              : input.url,
      });
      throw error;
    }
  };
}

export function createDatadogFeatureFlagProvider() {
  const applicationId = process.env.REACT_APP_DD_APPLICATION_ID;
  const clientToken = process.env.REACT_APP_DD_CLIENT_TOKEN;
  const site = process.env.REACT_APP_DD_SITE;
  const env = process.env.REACT_APP_DD_ENV;

  if (!applicationId || !clientToken) {
    console.warn(
      'Missing Datadog feature flag env vars (REACT_APP_DD_APPLICATION_ID, REACT_APP_DD_CLIENT_TOKEN). Feature flags will not be available.'
    );
    return null;
  }

  return new DatadogProvider({ applicationId, clientToken, env, site });
}

export async function initFeatureFlags(initialContext?: EvaluationContext) {
  console.info(
    `${FEATURE_FLAG_LOG_PREFIX} initializing provider`,
    getFeatureFlagEnvSummary()
  );

  const provider = createDatadogFeatureFlagProvider();

  if (!provider) {
    console.warn(`${FEATURE_FLAG_LOG_PREFIX} provider initialization skipped`);
    return;
  }

  registerFeatureFlagEventHandlers();
  registerFeatureFlagFetchLogging();

  if (initialContext) {
    await OpenFeature.setContext(initialContext);
  }

  try {
    await OpenFeature.setProviderAndWait(provider);
    console.info(
      `${FEATURE_FLAG_LOG_PREFIX} provider initialized`,
      provider.metadata
    );
  } catch (error) {
    console.error(
      `${FEATURE_FLAG_LOG_PREFIX} provider initialization failed`,
      error
    );
  }
}
